import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { LoadingSpinner } from '../../components/layout/LoadingSpinner';
import { ProfileImage } from '../../components/ui/ProfileImage';
import { useViewport } from '../../hooks/useViewport';
import { useCandidates } from '../../hooks/unified/useAdminQueries';
import { useCandidateApplications } from '../../hooks/unified/useCandidatesQueries';
import { CandidateStageManager } from '../../components/admin/CandidateStageManager';
import { EmailComposerModal } from '../../components/admin/EmailComposerModal';
import type { CandidateStage } from '../../services/unified/types';

export function AdminCandidatesPage() {
  const navigate = useNavigate();
  const { isMobile } = useViewport();
  const { data: candidates = [], isLoading, error } = useCandidates();
  const [searchTerm, setSearchTerm] = useState('');
  const [resumeStatusFilter, setResumeStatusFilter] = useState<'all' | 'has_resume' | 'no_resume'>('all');
  const [applicationStatusFilter, setApplicationStatusFilter] = useState<'all' | 'has_applications' | 'no_applications'>('all');
  const [videoFilter, setVideoFilter] = useState<'all' | 'has_video' | 'no_video'>('all');
  const [stageFilter, setStageFilter] = useState<CandidateStage | 'all'>('all');
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalData, setEmailModalData] = useState<{
    jobId: string;
    candidateId: string;
    candidateName: string;
    jobTitle: string;
    companyName: string;
    currentStage: CandidateStage;
  } | null>(null);

  // Fetch applications for selected candidate
  const { data: candidateApplicationsData, refetch: refetchCandidateApplications } = useCandidateApplications(
    selectedCandidateId || '',
  );

  const filteredCandidates = candidates.filter(candidate => {
    const matchesSearch = 
      (candidate.user.name && candidate.user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      candidate.user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesResumeStatus = 
      resumeStatusFilter === 'all' ||
      (resumeStatusFilter === 'has_resume' && candidate.resume_status !== 'not_uploaded') ||
      (resumeStatusFilter === 'no_resume' && candidate.resume_status === 'not_uploaded');
    
    const matchesApplicationStatus = 
      applicationStatusFilter === 'all' ||
      (applicationStatusFilter === 'has_applications' && candidate.total_applications > 0) ||
      (applicationStatusFilter === 'no_applications' && candidate.total_applications === 0);
    
    // Check if candidate has any applications with videos
    const hasVideo = candidate.applications?.some((app: any) => app.has_video) || false;
    const matchesVideoFilter = 
      videoFilter === 'all' ||
      (videoFilter === 'has_video' && hasVideo) ||
      (videoFilter === 'no_video' && !hasVideo);

    // Check if candidate has any applications in the selected stage
    const matchesStage = 
      stageFilter === 'all' ||
      candidate.applications?.some((app: any) => app.current_stage === stageFilter) || false;
    
    return matchesSearch && matchesResumeStatus && matchesApplicationStatus && matchesVideoFilter && matchesStage;
  });

  const handleOpenEmailModal = (
    jobId: string,
    candidateId: string,
    candidateName: string,
    jobTitle: string,
    companyName: string,
    currentStage: CandidateStage
  ) => {
    setEmailModalData({
      jobId,
      candidateId,
      candidateName,
      jobTitle,
      companyName,
      currentStage,
    });
    setShowEmailModal(true);
  };

  const handleCloseEmailModal = () => {
    setShowEmailModal(false);
    setEmailModalData(null);
  };

  const getResumeStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      submitted: 'bg-blue-100 text-blue-800',
      processing: 'bg-yellow-100 text-yellow-800',
      scanned: 'bg-green-100 text-green-800',
      error: 'bg-red-100 text-red-800',
      not_uploaded: 'bg-gray-100 text-gray-800',
    };

    const labels: Record<string, string> = {
      submitted: 'Submitted',
      processing: 'Processing',
      scanned: 'Scanned',
      error: 'Error',
      not_uploaded: 'No Resume',
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.not_uploaded}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Candidates</h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage and review candidate profiles and applications.
            </p>
          </div>
          <div className="flex items-center">
            <button className="w-full sm:w-auto bg-primary-600 hover:bg-primary-700 text-white px-4 py-2.5 rounded-md text-sm font-medium transition-colors min-h-[44px]">
              Export Candidates
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            Failed to load candidates: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        )}

        {/* Filters - Stacked on mobile */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="space-y-3 sm:space-y-0 sm:flex sm:flex-wrap sm:gap-4">
            <div className="flex-1 min-w-[200px]">
              <label htmlFor="search" className="sr-only">Search candidates</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  id="search"
                  type="text"
                  placeholder="Search by name, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                />
              </div>
            </div>
            
            {/* Filter dropdowns - 2 columns on mobile */}
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-4">
              <select
                value={resumeStatusFilter}
                onChange={(e) => setResumeStatusFilter(e.target.value as typeof resumeStatusFilter)}
                className="block w-full px-3 py-2.5 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-sm min-h-[44px]"
              >
                <option value="all">Resume</option>
                <option value="has_resume">Has Resume</option>
                <option value="no_resume">No Resume</option>
              </select>
              <select
                value={applicationStatusFilter}
                onChange={(e) => setApplicationStatusFilter(e.target.value as typeof applicationStatusFilter)}
                className="block w-full px-3 py-2.5 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-sm min-h-[44px]"
              >
                <option value="all">Applications</option>
                <option value="has_applications">Has Apps</option>
                <option value="no_applications">No Apps</option>
              </select>
              <select
                value={videoFilter}
                onChange={(e) => setVideoFilter(e.target.value as typeof videoFilter)}
                className="block w-full px-3 py-2.5 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-sm min-h-[44px]"
              >
                <option value="all">Video</option>
                <option value="has_video">Has Video</option>
                <option value="no_video">No Video</option>
              </select>
              <select
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value as CandidateStage | 'all')}
                className="block w-full px-3 py-2.5 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-sm min-h-[44px]"
              >
                <option value="all">Stage</option>
                <option value="Applied">Applied</option>
                <option value="Resume Review">Review</option>
                <option value="Shortlisted">Shortlisted</option>
                <option value="Interview Scheduled">Interview</option>
                <option value="Offer Extended">Offered</option>
                <option value="Hired">Hired</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats - 2 columns on mobile, 5 on desktop */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Candidates</p>
                <p className="text-lg font-semibold text-gray-900">{candidates.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">With Resume</p>
                <p className="text-lg font-semibold text-gray-900">
                  {candidates.filter(c => c.resume_status !== 'not_uploaded').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">With Applications</p>
                <p className="text-lg font-semibold text-gray-900">
                  {candidates.filter(c => c.total_applications > 0).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Applications</p>
                <p className="text-lg font-semibold text-gray-900">
                  {candidates.reduce((sum, c) => sum + c.total_applications, 0)}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">With Videos</p>
                <p className="text-lg font-semibold text-gray-900">
                  {candidates.filter(c => c.applications?.some((app: any) => app.has_video)).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Candidates - Card layout on mobile, table on desktop */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Mobile Card Layout */}
          {isMobile ? (
            <div className="divide-y divide-gray-200">
              {filteredCandidates.map((candidate) => (
                <div
                  key={candidate.user.id}
                  className="p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/admin/candidates/${candidate.user.id}`)}
                >
                  <div className="flex items-start gap-3">
                    <ProfileImage
                      src={candidate.user.avatar}
                      alt={candidate.user.name || 'User'}
                      name={candidate.user.name || 'Unknown'}
                      size="sm"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-medium text-gray-900 truncate">
                          {candidate.user.name || 'Unknown'}
                        </h4>
                        {getResumeStatusBadge(candidate.resume_status)}
                      </div>
                      <p className="text-xs text-gray-500 truncate mb-2">{candidate.user.email}</p>
                      
                      {/* Stats row */}
                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
                        <span><strong>{candidate.total_applications}</strong> apps</span>
                        {candidate.applications?.some((app: any) => app.has_video) && (
                          <span className="text-blue-600">
                            <strong>{candidate.applications?.filter((app: any) => app.has_video).length}</strong> videos
                          </span>
                        )}
                        <span>
                          {candidate.last_activity 
                            ? new Date(candidate.last_activity).toLocaleDateString()
                            : 'Never active'}
                        </span>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCandidateId(candidate.user.id);
                          }}
                          className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-md min-h-[36px]"
                        >
                          Applications
                        </button>
                        <Link
                          to={`/admin/candidates/${candidate.user.id}`}
                          className="px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-md min-h-[36px] flex items-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Profile
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Desktop Table Layout */
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Candidate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title & Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Resume Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Applications
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Video
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Active
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCandidates.map((candidate) => (
                    <tr 
                      key={candidate.user.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/admin/candidates/${candidate.user.id}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <ProfileImage
                            src={candidate.user.avatar}
                            alt={candidate.user.name || 'User'}
                            name={candidate.user.name || 'Unknown'}
                            size="sm"
                          />
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{candidate.user.name || 'Unknown'}</div>
                            <div className="text-sm text-gray-500">{candidate.user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {candidate.experiences?.[0]?.title || candidate.profile?.bio || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">{candidate.profile?.location || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getResumeStatusBadge(candidate.resume_status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{candidate.total_applications}</div>
                        {candidate.application_success_rate > 0 && (
                          <div className="text-xs text-gray-500">
                            {candidate.application_success_rate.toFixed(0)}% success
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {candidate.applications?.some((app: any) => app.has_video) ? (
                          <div className="flex items-center text-sm">
                            <svg className="h-5 w-5 text-blue-600 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <span className="text-blue-600 font-medium">
                              {candidate.applications?.filter((app: any) => app.has_video).length}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {candidate.last_activity 
                          ? new Date(candidate.last_activity).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCandidateId(candidate.user.id);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                        >
                          Applications
                        </button>
                        <Link
                          to={`/admin/candidates/${candidate.user.id}`}
                          className="text-primary-600 hover:text-primary-900"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Profile
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
          )}

          {filteredCandidates.length === 0 && (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No candidates found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || resumeStatusFilter !== 'all' || applicationStatusFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No candidates have registered yet.'
                }
              </p>
            </div>
          )}
        </div>

        {/* Candidate Applications Detail Modal */}
        {selectedCandidateId && candidateApplicationsData && candidateApplicationsData.candidate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-lg w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg sm:text-2xl font-semibold text-gray-900 truncate">
                    {candidateApplicationsData.candidate?.user?.name || 'Candidate'}
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1 truncate">
                    {candidateApplicationsData.candidate?.user?.email || 'No email'}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedCandidateId(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 min-w-[44px] min-h-[44px] flex items-center justify-center ml-2"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {candidateApplicationsData.applications.length > 0 ? (
                  <div className="space-y-6">
                    {candidateApplicationsData.applications.map((application) => (
                      <div key={application.id} className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              Job Application
                            </h3>
                            <p className="text-sm text-gray-600">
                              Applied: {new Date(application.applied_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => navigate(`/admin/jobs/${application.job_id}`)}
                              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                            >
                              View Job
                            </button>
                            <button
                              onClick={() => handleOpenEmailModal(
                                application.job_id,
                                application.candidate_id,
                                candidateApplicationsData.candidate?.user?.name || 'Candidate',
                                'Job Title', // You may need to fetch job details
                                'Company Name', // You may need to fetch company details
                                application.current_stage
                              )}
                              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm"
                            >
                              Send Email
                            </button>
                          </div>
                        </div>

                        {/* Stage Manager */}
                        <CandidateStageManager
                          application={application}
                          jobId={application.job_id}
                          onStageChange={() => {
                            // Refetch candidate applications to update the UI
                            refetchCandidateApplications();
                          }}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No applications</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      This candidate hasn't applied to any jobs yet.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Email Composer Modal */}
        {showEmailModal && emailModalData && (
          <EmailComposerModal
            isOpen={showEmailModal}
            onClose={handleCloseEmailModal}
            jobId={emailModalData.jobId}
            candidateId={emailModalData.candidateId}
            candidateName={emailModalData.candidateName}
            jobTitle={emailModalData.jobTitle}
            companyName={emailModalData.companyName}
            currentStage={emailModalData.currentStage}
          />
        )}
      </div>
    </AdminLayout>
  );
}